import { useEffect } from 'react';
import { Outlet } from 'react-router-dom';

import { hideNativeSplash } from 'src/utils/capacitor';

import { LocalizationProvider } from 'src/locales';
import { themeConfig, ThemeProvider } from 'src/theme';
import { I18nProvider } from 'src/locales/i18n-provider';

import { Snackbar } from 'src/components/snackbar';
import { ProgressBar } from 'src/components/progress-bar';
import { MotionLazy } from 'src/components/animate/motion-lazy';
import { BackendErrorMonitor } from 'src/components/error-reporter';
import { AndroidBackButton } from 'src/components/android-back-button';
import { SettingsDrawer, defaultSettings, SettingsProvider } from 'src/components/settings';

import { OtaUpdateSheet } from 'src/sections/artham/shared/ota-update-sheet';

import { AuthProvider } from 'src/auth/context/firebase';

// ----------------------------------------------------------------------

/**
 * App-wide provider tree. Lives INSIDE the router (as the root route element)
 * because several providers - AuthProvider, ThemeProvider (via useTranslate),
 * ProgressBar - call React Router hooks that require router context.
 */
export function RootLayout() {
  useEffect(() => {
    // Hand off from the native splash to the in-app UI. Wait two frames so the
    // webview has actually painted its first screen before the splash lifts -
    // otherwise the native splash hiding could briefly reveal a blank frame.
    const raf = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        hideNativeSplash();
      });
    });
    return () => cancelAnimationFrame(raf);
  }, []);

  return (
    <I18nProvider>
      <AuthProvider>
        <SettingsProvider defaultSettings={defaultSettings}>
          <LocalizationProvider>
            <ThemeProvider
              modeStorageKey={themeConfig.modeStorageKey}
              defaultMode={themeConfig.defaultMode}
            >
              <MotionLazy>
                <AndroidBackButton />
                <Snackbar />
                <ProgressBar />
                <BackendErrorMonitor />
                <SettingsDrawer defaultSettings={defaultSettings} />
                <OtaUpdateSheet />
                <Outlet />
              </MotionLazy>
            </ThemeProvider>
          </LocalizationProvider>
        </SettingsProvider>
      </AuthProvider>
    </I18nProvider>
  );
}
